import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { toArabicDigits } from '../../../utils/numbers';
import Icon from '../../../components/AppIcon';

const CircleDistribution = ({ data }) => {
  const hasData = Array.isArray(data) && data.length > 0;

  if (!hasData) {
    return (
      <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
        <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground mb-4 md:mb-6">
          توزيع المحتوى حسب الدوائر
        </h3>
        <div className="w-full h-64 md:h-80 lg:h-96 flex flex-col items-center justify-center text-center">
          <Icon name="PieChart" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4" style={{ fontFamily: 'Amiri, serif' }}>
            لا توجد مقالات منشورة بعد
          </p>
          <p className="text-sm text-muted-foreground max-w-md" style={{ fontFamily: 'Amiri, serif' }}>
            عند نشر مقالاتك في دوائر مختلفة، سيظهر توزيعها هنا
          </p>
        </div>
      </div>
    );
  }

  /* Distinct palette – no duplicates; enough for ~20 circles */
  const COLORS = [
    '#C5A059', /* primary gold */
    '#6B5B47', /* dark brown */
    '#4A6741', /* forest green */
    '#B8860B', /* dark goldenrod */
    '#8B7355', /* warm brown */
    '#4A7C59', /* sage green */
    '#A0522D', /* sienna */
    '#D4956A', /* peach */
    '#7D8471', /* olive gray */
    '#5C6B8A', /* slate blue */
    '#9B6B5C', /* terracotta */
    '#3D5C4A', /* deep green */
    '#C17F59', /* copper */
    '#6B8E7D', /* muted teal */
    '#B56B4A', /* burnt orange */
    '#5A4E6B', /* purple-gray */
    '#8B8B6B', /* khaki */
    '#7A5C4A', /* walnut */
    '#4A6B7A', /* steel blue */
    '#9B7A5C', /* tan */
  ];

  const LABEL_COLOR = '#2C2B29';

  const total = data?.reduce((sum, d) => sum + (d?.value ?? 0), 0) || 1;
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const value = payload?.[0]?.value ?? 0;
      const percent = ((value / total) * 100).toFixed(0);
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-warm-lg">
          <p className="text-sm font-medium text-foreground">{payload?.[0]?.name}</p>
          <p className="text-xs text-muted-foreground">
            المقالات: <span className="font-bold text-foreground">{toArabicDigits(value)}</span>
            {' · '}
            {toArabicDigits(percent)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
      <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground mb-4 md:mb-6">
        توزيع المحتوى حسب الدوائر
      </h3>
      <div className="w-full h-64 md:h-80 lg:h-96" aria-label="مخطط توزيع الدوائر">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="45%"
              nameKey="name"
              dataKey="value"
              outerRadius="65%"
              innerRadius="40%"
              fill="var(--color-primary)"
              paddingAngle={1}
            >
              {data?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="bottom" 
              height={48}
              wrapperStyle={{ fontSize: '13px', fontFamily: 'Amiri, serif' }}
              content={({ payload }) => (
                <ul className="recharts-default-legend" style={{ padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 24px', listStyle: 'none' }}>
                  {payload?.map((entry, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: entry.color,
                          flexShrink: 0
                        }}
                      />
                      <span style={{ color: LABEL_COLOR }}>{entry.value}</span>
                    </li>
                  ))}
                </ul>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CircleDistribution;